import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';


import { useEscapeLayer } from '@/components/escapeLayer';
import { HaiIcon } from '@/components/icons';
import { changeAppLanguage } from '@/i18n';
import { useLibraryDateFormat } from '@/features/assets/libraryDate';
import { pointsExpiryDisplay } from '@/lib/pointsExpiryDisplay';
import { services } from '@/services';
import { REDEEM_CODES_SUPPORTED } from '@/services/redeemCodesService';
import { RedeemCodeDialog } from '@/features/redeem/RedeemCodeDialog';
import { useReferralAdminGate } from '@/features/referral/useReferralAdminGate';
import { usePointsGate } from '@/features/points/usePointsGate';
import { clearClientAuthState } from '@/lib/clearClientAuthState';
import type { LocaleCode } from '@/services/prefsStore';
import { readThemePref, setThemeMode, type ThemePref } from '@/styles/themeMode';
import { useAuthStore } from '@/stores/authStore';
import { openReportDialog } from '@/stores/reportDialogStore';
import { useGroupContextStore } from '@/stores/groupContextStore';
import { useWalletStore } from '@/stores/walletStore';
import { deriveWalletUsagePct } from '@/types';

/**
 * SidebarAccountMenu — R164 側邊欄底部帳號選單(取代 TopNav 右上 ThemeMenu)。
 * -----------------------------------------------------------------------------
 * 對齊 D3 `專案_工作區.html` lines 684-704(.acct-wrap):
 *   - trigger:avatar 圓(首字)+ displayName + email + unfold 雙箭頭;
 *     collapsed 時只顯 avatar 置中。
 *   - menu 開向**上方**(collapsed 時開右側):head(大 avatar + 名字 + email)
 *     → 個人資料 / 訂閱與用量 / 說明 → 語言切換 → [admin] 建立合約 → 分隔 → 登出。
 *
 * 與 D3 的差異(拍板依據):
 *   - 「訂閱與用量 NN%」item **R196 補上**,原本是**不可點的純顯示列**(`UsageRow`)——
 *     當時沒有可去的頁。**Mentor Creative 點數起**這一列在該環境有點數功能時可點、導到
 *     點數頁 `/points`(owner 2026-10-20:「點擊訂閱與用量後,會跳轉到新頁面」);沒有
 *     (probe 判定路由不存在 / 沒接 Mentor)維持純顯示列 —— 不留點了沒反應的列。
 *   - 語言切換保留(現有功能,D3 mock 無語言概念)。
 *   - platform-admin 建約入口保留(R106 合約制營運入口,gate 在 role)。
 *
 * 語言 / 登出邏輯原樣搬自 ThemeMenu(該檔隨 TopNav 退場刪除):
 * changeLanguage → prefsStore.save;logout → clearClientAuthState → /login。
 */

export interface SidebarAccountMenuProps {
  collapsed: boolean;
}

/**
 * 用量比例算不出來、連剩餘點數都拿不到時的佔位(em dash)。
 *
 * R277(docs/100 §B-3)後試玩沙盒走「剩 N 點」,只有 `available` 非有限數
 * (理論上不會)才落到這裡。**不放 0%**(那是謊)。
 */
const USAGE_UNKNOWN = '—';

export function SidebarAccountMenu({ collapsed }: SidebarAccountMenuProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  // R276 — 兌換序號對話框。state 放在**選單外**:點選單項會關選單(AccountPanel
  // unmount),對話框要活得比它久,不然開一幀就跟著選單一起消失。
  const [redeemOpen, setRedeemOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const user = useAuthStore((s) => s.user);
  const displayName = user?.displayName ?? '';
  const email = user?.email ?? '';
  const initial =
    user?.displayName?.charAt(0).toUpperCase() ?? user?.email?.charAt(0).toUpperCase() ?? '?';

  // 點外面關閉(沿 ThemeMenu 慣例)
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!(event.target instanceof globalThis.Node)) return;
      if (containerRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);
  // Escape 關閉 — R393 走全站分層(只收最上層;不再自己掛 document keydown)
  useEscapeLayer(() => setOpen(false), open);

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t('userMenu.open')}
        data-testid="sidebar-account-trigger"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--slate-100)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '8px 0' : '8px 10px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderRadius: 11,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background .12s',
        }}
      >
        <Avatar initial={initial} />
        {!collapsed && (
          <>
            <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: 'var(--slate-800)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {displayName}
              </span>
              <span
                style={{
                  fontSize: 10.5,
                  color: 'var(--ink-500)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginTop: 1,
                }}
              >
                {email}
              </span>
            </span>
            <UnfoldIcon />
          </>
        )}
      </button>
      {open && (
        <AccountPanel
          collapsed={collapsed}
          initial={initial}
          displayName={displayName}
          email={email}
          onClose={() => setOpen(false)}
          onOpenRedeem={() => setRedeemOpen(true)}
        />
      )}
      {redeemOpen && <RedeemCodeDialog onClose={() => setRedeemOpen(false)} />}
    </div>
  );
}

function AccountPanel({
  collapsed,
  initial,
  displayName,
  email,
  onClose,
  onOpenRedeem,
}: {
  collapsed: boolean;
  initial: string;
  displayName: string;
  email: string;
  onClose: () => void;
  onOpenRedeem: () => void;
}) {
  const { t, i18n } = useTranslation('common');
  const navigate = useNavigate();
  // R106 — platform-admin 入口 gate(docs/65:role 在 me.profile.role,authStore.user 鏡射)。
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  // R392 — 推薦分潤入口是雙閘:role=admin **且** referral flag on(probe A22;
  // flag 關 / BE 未部署 = 整個入口隱藏,owner 拍板 — 不是點了會噴錯的選單)。
  const referralGate = useReferralAdminGate(isAdmin);
  // Mentor Creative 點數 — 「訂閱與用量」列可不可點(probe `GET /points/packages`,session 級快取)。
  // ⚠️ 只有確認沒有(`disabled`)才不給點;探測中(`unknown`)照樣給點 —— 那支要經 BE 問 Mentor,
  // 冷啟十幾秒,等它回來才給點 = 那一列死十幾秒。先點進去也沒壞處:點數頁自己誠實顯示狀態。
  const pointsGate = usePointsGate();

  // R196 — 用量%(D3 .acct-pct):資料走現成的 per-(user,group) wallet(RequireAuth 已訂
  // `users/{cuid}/wallets/{gid}` onSnapshot + `/me` 派生),**不新增任何 BE 呼叫**。
  const currentGroupId = useGroupContextStore((s) => s.currentGroupId);
  const wallet = useWalletStore((s) =>
    currentGroupId ? s.wallets.get(currentGroupId) : undefined,
  );
  const usagePct = deriveWalletUsagePct(wallet);
  // R277(docs/100 §B-3 owner 拍板)— 合約工作區(cap>0)顯 %;**試玩(cap=0)顯
  // 「剩 N 點」**:「試玩不顯示 %,後端不會提供試玩分母」= settings.16 需求正式否決,
  // R198 的「—」佔位讓位給剩餘點數(全站唯一的點數數字,拍板的刻意例外;序號兌換
  // 〔R276〕兌進試玩沙盒後這格會跟著動)。
  // wallet 尚未載入 → 整列不渲染(那是「還在載」不是「沒有資料」)。
  const usageValue = wallet
    ? usagePct !== null
      ? `${usagePct}%`
      : Number.isFinite(wallet.available)
        ? t('userMenu.usageRemaining', { count: wallet.available })
        : USAGE_UNKNOWN
    : null;
  // R391 — 帶效期的兌換點批次(docs/74 §1.A):多批取**最近一批**(mapper 已升冪),
  // 顯示「其中 N 點將於 M/D 到期」;分鐘級批次(R391.2)多顯 HH:mm。expiresOn null
  // (doc 殼換算失敗)的批次跳過 —— 不編日期;完全沒有批次就整行不渲染。
  const nearestExpiring = (wallet?.expiringLots ?? []).find((l) => l.expiresOn !== null) ?? null;
  // 顯示形狀走全站單點(台北午夜 = 天級;否則分鐘級)。
  const nearestExpiry = nearestExpiring
    ? pointsExpiryDisplay(nearestExpiring.expiresAt ?? null, nearestExpiring.expiresOn)
    : null;
  // R393 — CIS 日期寫法(每個顯示日期都要有年:`11月 22, 2026`)。台北日字串用 regex 拆
  // 再交 `fmtFields` 組句,**刻意不過 `Date`**(R391:西半球時區會把日期挪一天)。
  const { fmtFields } = useLibraryDateFormat();
  const fmtTaipeiDate = (isoDate: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
    return m ? fmtFields({ y: Number(m[1]), m: Number(m[2]), d: Number(m[3]), hh: '', mm: '' }) : isoDate;
  };

  const currentLocale: LocaleCode = (() => {
    const lng = i18n.resolvedLanguage ?? i18n.language ?? 'zh-TW';
    if (lng === 'en' || lng === 'ja') return lng;
    return 'zh-TW';
  })();

  // 外觀(R218 `themeMode` 的第一個 production consumer;在此之前只有 dev console
  // 改 localStorage 這條路 → 跟隨 OS 進深色的人在 app 內沒有回到淺色的出口)。
  // 真相在 `<html data-theme-pref>`,state 只是回顯;setThemeMode 同步寫 localStorage
  // + 套 DOM,不需要 reload。
  const [themePref, setThemePref] = useState<ThemePref>(() => readThemePref());
  const handleThemeChange = (next: ThemePref) => {
    if (next === themePref) return;
    setThemeMode(next);
    setThemePref(next);
  };

  const handleLanguageChange = async (next: LocaleCode) => {
    if (next === currentLocale) return;
    try {
      // R413 — en / ja 字典是用到才載入:先載字典再切(直接 changeLanguage 會先顯示繁中)
      await changeAppLanguage(next);
    } catch (err) {
      console.error('[i18n] changeLanguage failed', err);
      return;
    }
    // 同步寫進 prefsStore(未來 BE `/me/preferences` 接過去時 caller 不需動)
    try {
      await services.prefsStore.save({ locale: next });
    } catch (err) {
      console.error('[prefsStore] save locale failed', err);
    }
  };

  const handleLogout = async () => {
    onClose();
    // 主動登出 — 清空所有 client-side auth state,順序收斂在 clearClientAuthState,
    // 與 services/index.ts 的 onAuthLost 對齊(避免兩條 auth-exit 路徑分歧)。
    await clearClientAuthState();
    navigate('/login', { replace: true });
  };

  const handleNavigate = (to: string) => () => {
    onClose();
    navigate(to);
  };

  // 展開:開上方(D3 .acct-menu);collapsed:開右側(D3 collapsed override)
  //
  // ⚠️ 展開態刻意**靠左錨定**:選單 224 寬,而側欄只有 216(`SIDEBAR_WIDTH_EXPANDED`)
  // 且底部容器左右各 8 padding → 舊寫法 `right:-6` 把左緣推到 x = 216−8+6−224 = **−10**,
  // 也就是整個選單有 10px 在視窗外(頭像與各列 icon 的左側被切掉)。改成 `left:-6` 是同一個
  // 6px 外擴、只是換一側:選單佔 x = 2…226,溢出的那 10px 落在「有空間的那邊」(內容區,
  // 選單 z-index 70 本來就蓋在上面)。**不要改回 right 錨定**,除非選單寬度 ≤ 214。
  const position: CSSProperties = collapsed
    ? { bottom: 0, left: 'calc(100% + 10px)' }
    : { bottom: 'calc(100% + 8px)', left: -6 };

  return (
    <div
      role="menu"
      aria-label={t('userMenu.label')}
      data-testid="sidebar-account-menu"
      style={{
        position: 'absolute',
        ...position,
        width: 224,
        background: 'var(--ink-900)',
        border: '1px solid var(--hairline)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-lg)',
        padding: 6,
        zIndex: 70,
      }}
    >
      {/* head(D3 .acct-head):大 avatar + 名字 + email */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px 10px',
          borderBottom: '1px solid var(--slate-100)',
          marginBottom: 4,
        }}
      >
        <Avatar initial={initial} size={40} />
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: 'var(--text-strong)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {displayName}
          </div>
          <div
            style={{
              // D3 `.acct-hmail`:11.5 / #5B697F(次文字;CIS 文字只有兩層)
              fontSize: 11.5,
              color: 'var(--ink-500)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: 1,
            }}
          >
            {email}
          </div>
        </div>
      </div>

      <MenuItem
        icon={<PersonIcon />}
        label={t('userMenu.profile')}
        onClick={handleNavigate('/profile')}
        testid="account-menu-profile"
      />
      {usageValue !== null && (
        <UsageRow
          label={t('userMenu.usage')}
          value={usageValue}
          onClick={pointsGate !== 'disabled' ? handleNavigate('/points') : undefined}
        />
      )}
      {usageValue !== null && nearestExpiring && nearestExpiry && (
        <div data-testid="account-menu-expiring" style={EXPIRING_ROW_STYLE}>
          {nearestExpiry.kind === 'datetime'
            ? t('userMenu.expiringSoonAt', {
                count: nearestExpiring.amount,
                date: fmtTaipeiDate(nearestExpiry.date),
                time: nearestExpiry.time,
              })
            : t('userMenu.expiringSoon', {
                count: nearestExpiring.amount,
                date: fmtTaipeiDate(nearestExpiry.date),
              })}
        </div>
      )}
      {/* R276 — 兌換序號(gate:BE 未交付前不渲染 — R246.3:永遠不能點的入口只會讓
          每個人問一次「這是什麼」)。放在「訂閱與用量」下面:序號兌的點數就是那條
          百分比的分子,兌完它會跟著降。 */}
      {REDEEM_CODES_SUPPORTED && (
        <MenuItem
          icon={<TicketIcon />}
          label={t('userMenu.redeem')}
          onClick={() => {
            onClose();
            onOpenRedeem();
          }}
          testid="account-menu-redeem"
        />
      )}
      <MenuItem
        icon={<HelpIcon />}
        label={t('userMenu.help')}
        onClick={handleNavigate('/help')}
        testid="account-menu-help"
      />
      {/* R249 — 回報問題。放在「說明」下面:兩者是同一類意圖(遇到不懂 / 遇到壞掉),
          使用者找不到說明時的下一步就是回報。點了關選單再開對話框(選單是 absolute
          定位、遮罩 z-index 更高,不關的話選單會留在遮罩下方成為視覺殘留)。 */}
      <MenuItem
        icon={<MegaphoneIcon />}
        label={t('report.menuLabel')}
        onClick={() => {
          onClose();
          openReportDialog();
        }}
        testid="account-menu-report"
      />
      {isAdmin && (
        <MenuItem
          icon={<DocIcon />}
          label={t('userMenu.contractAdmin')}
          onClick={handleNavigate('/admin/contracts')}
          testid="account-menu-contract-admin"
        />
      )}
      {/* R276 — 序號管理(admin;同 gate)。 */}
      {isAdmin && REDEEM_CODES_SUPPORTED && (
        <MenuItem
          icon={<TicketIcon />}
          label={t('userMenu.redeemAdmin')}
          onClick={handleNavigate('/admin/redeem-codes')}
          testid="account-menu-redeem-admin"
        />
      )}
      {/* R392 — 推薦分潤管理(admin ∧ referral flag on;flag 關 / BE 未部署則整個
          入口不出現 — docs/128 §0 owner 拍板)。 */}
      {isAdmin && referralGate === 'enabled' && (
        <MenuItem
          icon={<HandshakeIcon />}
          label={t('userMenu.referralAdmin')}
          onClick={handleNavigate('/admin/referral')}
          testid="account-menu-referral-admin"
        />
      )}

      {/* 外觀:系統 / 淺色 / 深色(themeMode.setThemeMode → `<html data-theme>`) */}
      <PrefSectionHeader label={t('userMenu.appearance.title')} />
      <div
        role="radiogroup"
        aria-label={t('userMenu.appearance.title')}
        data-testid="account-menu-theme"
        style={PREF_ROW_STYLE}
      >
        <PrefPill
          label={t('userMenu.appearance.system')}
          checked={themePref === 'system'}
          onSelect={() => handleThemeChange('system')}
        />
        <PrefPill
          label={t('userMenu.appearance.light')}
          checked={themePref === 'light'}
          onSelect={() => handleThemeChange('light')}
        />
        <PrefPill
          label={t('userMenu.appearance.dark')}
          checked={themePref === 'dark'}
          onSelect={() => handleThemeChange('dark')}
        />
      </div>

      {/* 語言切換(現有功能保留;D3 mock 無語言概念) */}
      <PrefSectionHeader label={t('userMenu.language.title')} />
      <div
        role="radiogroup"
        aria-label={t('userMenu.language.title')}
        data-testid="account-menu-language"
        style={PREF_ROW_STYLE}
      >
        <PrefPill
          label={t('userMenu.language.zhTW')}
          checked={currentLocale === 'zh-TW'}
          onSelect={() => handleLanguageChange('zh-TW')}
        />
        <PrefPill
          label={t('userMenu.language.en')}
          checked={currentLocale === 'en'}
          onSelect={() => handleLanguageChange('en')}
        />
        <PrefPill
          label={t('userMenu.language.ja')}
          checked={currentLocale === 'ja'}
          onSelect={() => handleLanguageChange('ja')}
        />
      </div>

      <div style={{ height: 1, background: 'var(--slate-100)', margin: '2px 4px 4px' }} />
      {/* R393 — 登出不是刪除 / 錯誤:CIS 玫瑰色專屬破壞性動作,D3 `.acct-item.logout` 也是
          中性色(icon #5B697F)→ 與其他選單項同色,不再走 danger。 */}
      <MenuItem
        icon={<LogoutIcon />}
        label={t('userMenu.logout')}
        onClick={() => void handleLogout()}
        testid="account-menu-logout"
      />
    </div>
  );
}

/** 圓形 avatar(D3 .acct-avatar):中性底 + 首字。 */
function Avatar({ initial, size = 28 }: { initial: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        width: size,
        height: size,
        flex: `0 0 ${size}px`,
        borderRadius: 9999,
        background: 'var(--slate-150)',
        // CIS 次文字(#5B697F 在 #EDF2F7 上 4.96:1;--slate-500 只有 4.3:1)
        color: 'var(--ink-500)',
        fontSize: size >= 40 ? 14 : 11,
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {initial}
    </span>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  danger = false,
  testid,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  testid: string;
}) {
  // danger 字走 rose-700(CIS 刪除 / 錯誤字色;深色主題會翻亮,rose-600 在深底只有 ~3.7:1)
  const baseColor = danger ? 'var(--rose-700)' : 'var(--ink-300)';
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      data-testid={testid}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = danger ? 'var(--rose-50)' : 'var(--slate-100)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '8px 10px',
        borderRadius: 9,
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontSize: 12.5,
        fontWeight: 500,
        color: baseColor,
        textAlign: 'left',
        transition: 'background .12s',
      }}
    >
      {icon}
      {label}
    </button>
  );
}

/**
 * 「訂閱與用量 NN%」列(D3 `.acct-item` + `.acct-pct`)。
 *
 * 幾何沿 `MenuItem`(padding / gap / fontSize 一致)讓選單看起來是同一排。
 *   - 有 `onClick`(該環境有點數功能)→ `<button role="menuitem">`,hover 同 `MenuItem`,
 *     點了導到點數頁。
 *   - 沒有 → 維持 R196 的**純顯示列**(不是 `<button>`、無 hover、無 cursor):做成可點卻
 *     點了沒反應更糟(R196 拍板)。
 *
 * `value` 兩種形態(R198 / R277):有月額度 → `"38%"`;試玩沙盒 → 「剩 N 點」。
 */
function UsageRow({ label, value, onClick }: { label: string; value: string; onClick?: () => void }) {
  const rowStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    padding: '8px 10px',
    fontSize: 12.5,
    fontWeight: 500,
    color: 'var(--ink-300)',
  };
  const pill = (
    <span
      data-testid="account-menu-usage-pct"
      style={{
        marginLeft: 'auto',
        fontSize: 11.5,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        // teal 文字走 --concept-fg(深色 #5EEAD4;--tide-primary-hover 深色 #12897E 只有 ~4:1)
        color: 'var(--concept-fg)',
        background: 'var(--concept-soft)',
        borderRadius: 9999,
        padding: '1px 8px',
      }}
    >
      {value}
    </span>
  );
  if (onClick) {
    return (
      <button
        type="button"
        role="menuitem"
        onClick={onClick}
        data-testid="account-menu-usage"
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'var(--slate-100)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
        }}
        style={{
          ...rowStyle,
          borderRadius: 9,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontFamily: 'inherit',
          transition: 'background .12s',
        }}
      >
        <SparkIcon />
        {label}
        {pill}
      </button>
    );
  }
  return (
    <div data-testid="account-menu-usage" style={rowStyle}>
      <SparkIcon />
      {label}
      {pill}
    </div>
  );
}

/** R391 — 「其中 N 點將於 M/D 到期」列(UsageRow 的補充行;非互動)。
 * 前景走 `--amber-700`(R266:`--warn` 只能當淡底,文字要用會隨主題翻的 700 階)。 */
const EXPIRING_ROW_STYLE: CSSProperties = {
  padding: '0 10px 6px 34px', // 34 = MenuItem padding 10 + icon 15 + gap 9 → 與標籤字首對齊
  // 整句可讀內文 → CIS ≥ 11px
  fontSize: 11,
  lineHeight: 1.5,
  color: 'var(--amber-700)',
};

/** 偏好分段的小標(外觀 / 語言共用)。 */
function PrefSectionHeader({ label }: { label: string }) {
  return (
    <div
      style={{
        margin: '4px 0',
        padding: '6px 10px 2px',
        borderTop: '1px solid var(--slate-100)',
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: '.06em',
        color: 'var(--ink-500)',
      }}
    >
      {label}
    </div>
  );
}

/**
 * 偏好三選一那一排的容器(外觀 / 語言共用)。
 *
 * ⚠️ **`flexWrap` 不是裝飾,拿掉就是 bug**:三顆藥丸的總寬完全由譯文長度決定,而選單
 * 內可用寬度是固定的(224 − 邊框 2 − 選單 padding 12 − 本列 padding 12 = 198px)。
 * 英文語系的語言列「Traditional Chinese / English / Japanese」單行需要 ≈214px →
 * 不換行時最右邊的 Japanese 被擠出選單邊界(2026-08-18 dev 回報的症狀)。其餘五種組合
 * (中/日語言列、三語外觀列)最寬 159px 都放得下,換行只在真的放不下的那一列生效 →
 * **修英文不會動到中文 / 日文的版面**。
 *
 * 與 `PrefPill` 的 `flex:'1 1 auto'` 是一組,不可拆開改回 `flex:1`:`flex:1` 的
 * basis 是 0,換行演算法會判定「永遠放得下」→ 結果是三顆等寬 + 文字被截斷,不是換行。
 */
const PREF_ROW_STYLE: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  padding: '0 6px 6px',
};

/** 三選一藥丸(外觀 / 語言共用;`radiogroup` 內的 `radio`)。 */
function PrefPill({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      style={{
        /* basis = 內容寬(不是 0)→ 放不下的那一列才會換行(見 PREF_ROW_STYLE);
         * grow 1 讓同一行的藥丸補滿整列寬,放得下時外觀與舊版一致。 */
        flex: '1 1 auto',
        padding: '5px 4px',
        borderRadius: 8,
        fontSize: 11,
        fontWeight: checked ? 700 : 500,
        // CIS 選取態:teal-600 框 + teal-50 底 + teal-700 字(--concept-fg 深色會翻亮)
        color: checked ? 'var(--concept-fg)' : 'var(--ink-400)',
        background: checked ? 'var(--concept-soft)' : 'transparent',
        border: `1px solid ${checked ? 'var(--tide-600)' : 'var(--hairline)'}`,
        cursor: 'pointer',
        /* 最後一道保險:單一標籤長到一整列都放不下時用省略號收尾,不讓它衝出選單。
         * 現有六種組合都不會走到這裡(最長的 Traditional Chinese ≈104px < 198px)。 */
        whiteSpace: 'nowrap',
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {label}
    </button>
  );
}

/**
 * 選單 icon — R393 CIS「圖示單一來源」:HaiIcon 有的(個人資料 / 訂閱與用量 / 合約 / 登出 /
 * unfold)一律引用,不再自繪一份。HaiIcon 沒有的四顆(說明 / 序號 / 回報 / 推薦分潤)
 * 暫留本檔,但線寬對齊 HaiIcon 的 1.6(同一份選單不得混兩種線寬);待主迴圈補進
 * `icons.tsx` 後再改引用。
 */
const MENU_ICON_SIZE = 15;
const menuIconStyle: CSSProperties = { flexShrink: 0 };

const iconProps = {
  width: MENU_ICON_SIZE,
  height: MENU_ICON_SIZE,
  viewBox: '0 0 16 16',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  style: menuIconStyle,
  'aria-hidden': true,
} as const;

function PersonIcon() {
  return <HaiIcon name="user" size={MENU_ICON_SIZE} style={menuIconStyle} />;
}

function MegaphoneIcon() {
  // 24 格 viewBox:線寬 2.4 × 15/24 ≈ 1.5px,與 16 格 1.6 × 15/16 渲染同粗
  return (
    <svg
      width={MENU_ICON_SIZE}
      height={MENU_ICON_SIZE}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={menuIconStyle}
      aria-hidden
    >
      <path d="M3 11v2a1 1 0 0 0 1 1h2l3.5 3.5V7.5L6 11H4a1 1 0 0 0-1 1Z" />
      <path d="M14 8.5a4 4 0 0 1 0 7" />
      <path d="M17 6a7 7 0 0 1 0 12" />
    </svg>
  );
}

function HelpIcon() {
  return (
    <svg {...iconProps}>
      <circle cx={8} cy={8} r={6} />
      <path d="M6 7 a2 2 0 1 1 3 1.5 L8 9 L8 10 M8 12 v.01" />
    </svg>
  );
}

/** D3 訂閱與用量 icon(四角火花 = HaiIcon `star`,path 逐字相同)。 */
function SparkIcon() {
  return <HaiIcon name="star" size={MENU_ICON_SIZE} style={menuIconStyle} />;
}

/** R276 兌換序號 icon(票券:圓角框 + 中間虛線撕線)。 */
function TicketIcon() {
  return (
    <svg {...iconProps}>
      <rect x={2} y={4.5} width={12} height={7} rx={1.5} />
      <path d="M8 5.5 v1 M8 7.5 v1 M8 9.5 v1" />
    </svg>
  );
}

/** 工作區合約(文件)= HaiIcon `rpt`。 */
function DocIcon() {
  return <HaiIcon name="rpt" size={MENU_ICON_SIZE} style={menuIconStyle} />;
}

/** R392 — 推薦分潤管理(兩個人形 + 連結線,示意「業務 × 客戶」歸屬)。 */
function HandshakeIcon() {
  return (
    <svg {...iconProps}>
      <circle cx={5} cy={5.5} r={2} />
      <circle cx={11} cy={5.5} r={2} />
      <path d="M2 13.5 C2 10.8 3.4 9.5 5 9.5 C6 9.5 6.6 9.9 7 10.5 M14 13.5 C14 10.8 12.6 9.5 11 9.5 C10 9.5 9.4 9.9 9 10.5" />
      <path d="M6.5 12.5 H9.5" />
    </svg>
  );
}

function LogoutIcon() {
  return <HaiIcon name="logout" size={MENU_ICON_SIZE} style={menuIconStyle} />;
}

/** D3 .sb-unfold 雙 chevron(上下)= HaiIcon `unfold`。 */
function UnfoldIcon() {
  return <HaiIcon name="unfold" style={{ flexShrink: 0, color: 'var(--ink-500)' }} />;
}
